import React, { useState } from "react";
import { fetchFeedPostFeedback } from "../../api";
import TappableArabic from "../TappableArabic";
import FushaFeedback from "./FushaFeedback";
import { stripHarakat } from "../CallChat";
import { arabicDigits, arabicRelativeTime } from "../../utils/arabicNumbers";
import VotePill from "./VotePill";

/** The mockup's five monogram tint pairs, hashed from user id. */
export function avatarTint(userId) {
  const slot = ((userId || 0) % 5) + 1;
  return { background: `var(--mj-av${slot}-bg)`, color: `var(--mj-av${slot}-fg)` };
}

/**
 * PostCard — one post as the mockup's `.post` card: flat mj surface, green
 * hairline, hover border in ink-600; author row on top, Amiri 21px/2.15 body
 * (words tappable → Word Bank), then the action row (vote pill, comments
 * chip, report, delete).
 *
 * Props:
 *   post           — serialized post (my_vote/score/down_count from the vote API)
 *   showHarakat    — display toggle (never changes stored text)
 *   onVote         — (post, direction 1|-1) — declarative, optimistic upstream
 *   onOpenComments / onOpenProfile / onReport / onDelete
 *   commentsOpen   — highlights the comments chip
 *   isAdmin        — delete affordance on any post
 */
export default function PostCard({
  post,
  showHarakat = true,
  onDelete,
  onVote,
  onOpenComments,
  onOpenProfile,
  onReport,
  commentsOpen = false,
  isAdmin = false,
}) {
  const author = post.author || {};
  const bodyText = showHarakat ? post.body : stripHarakat(post.body);
  const mayDelete = post.is_mine || isAdmin;
  const published = post.status === "published";

  // "ملاحظات لك" — the author's private coaching notes, re-viewable any
  // time (my_feedback_count is only ever non-zero on the viewer's own
  // posts). Lazy-fetched from the author-gated /feedback/ endpoint.
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState(null);
  const toggleNotes = () => {
    setNotesOpen((open) => !open);
    if (notes === null) {
      fetchFeedPostFeedback(post.id)
        .then((d) => setNotes(d.reviews || []))
        .catch(() => setNotes([]));
    }
  };

  return (
    <article
      className="rounded-[14px] px-4 py-3 font-plex transition-colors"
      style={{ background: "var(--mj-surface)", border: "1px solid var(--mj-line)" }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--mj-ink-6)")}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--mj-line)")}
    >
      {/* .p-top — author row */}
      <div className="flex items-center gap-2 text-[12.5px]" style={{ color: "var(--mj-muted)" }}>
        <button
          onClick={() => onOpenProfile && onOpenProfile(author)}
          disabled={!onOpenProfile}
          className="flex items-center gap-2 min-w-0"
        >
          <span
            className="w-6 h-6 rounded-full grid place-items-center text-xs font-bold shrink-0"
            style={{ ...avatarTint(author.id), border: "1px solid var(--mj-line)" }}
            aria-hidden="true"
          >
            {author.initial || "؟"}
          </span>
          <span className="font-bold truncate" style={{ color: "var(--mj-cream)" }}>
            {author.display_name}
          </span>
        </button>
        <span className="opacity-60">•</span>
        <span>{arabicRelativeTime(post.published_at || post.created_at)}</span>
        {post.fully_vowelled && (
          <>
            <span className="opacity-60">•</span>
            <span
              className="font-arabic text-[11px] font-bold rounded-md px-2"
              style={{ color: "var(--mj-gold)", border: "1px solid var(--mj-gold-line)" }}
              title="منشور مشكول بالكامل"
            >
              مشكول
            </span>
          </>
        )}
        {post.status === "rejected" && (
          <span
            className="text-[11px] font-bold rounded-md px-2"
            style={{ color: "var(--mj-flag)", border: "1px solid var(--mj-line)" }}
          >
            مسودّة — غير منشور
          </span>
        )}
        {mayDelete && onDelete && (
          <button
            onClick={() => onDelete(post)}
            className="ms-auto p-1 rounded hover:brightness-125"
            style={{ color: "var(--mj-muted)" }}
            aria-label="Delete post"
            title="حذف"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.9 12.1A2 2 0 0116.1 21H7.9a2 2 0 01-2-1.9L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>

      {/* .p-body — Amiri, tappable words feed the Word Bank */}
      <TappableArabic
        text={bodyText}
        className="font-arabic text-[21px] leading-[2.15] mt-1.5"
        style={{ color: "var(--mj-body)" }}
      />

      {/* .p-actions */}
      <div className="flex items-center gap-2.5 mt-3 flex-wrap">
        <VotePill
          myVote={post.my_vote || 0}
          score={post.score ?? post.like_count}
          onVote={onVote ? (direction) => onVote(post, direction) : null}
          disabled={!published}
        />
        <button
          onClick={() => onOpenComments && onOpenComments(post)}
          disabled={!onOpenComments || !published}
          className="flex items-center gap-1.5 rounded-full px-3.5 py-1 text-[13px] font-semibold disabled:opacity-40"
          style={{
            background: "var(--mj-raised)",
            color: commentsOpen ? "var(--mj-gold)" : "var(--mj-muted)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--mj-ink-6)";
            e.currentTarget.style.color = "var(--mj-cream)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "var(--mj-raised)";
            e.currentTarget.style.color = commentsOpen ? "var(--mj-gold)" : "var(--mj-muted)";
          }}
          aria-label="Comments"
        >
          💬 {arabicDigits(post.comment_count)} {post.comment_count === 1 ? "إجابة" : "إجابات"}
        </button>
        {post.is_mine && post.my_feedback_count > 0 && (
          <button
            onClick={toggleNotes}
            className="flex items-center gap-1.5 rounded-full px-3.5 py-1 text-[13px] font-semibold"
            style={{
              background: notesOpen ? "var(--mj-gold-soft)" : "var(--mj-raised)",
              color: "var(--mj-gold)",
              border: notesOpen ? "1px solid var(--mj-gold-line)" : "1px solid transparent",
            }}
            title="ملاحظات المدقّق — لا يراها غيرُك"
            aria-expanded={notesOpen}
          >
            ✎ ملاحظات لك
          </button>
        )}
        {!post.is_mine && onReport && published && (
          <button
            onClick={() => onReport(post)}
            className="ms-auto flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px]"
            style={{ color: "var(--mj-muted)" }}
            aria-label="Report post"
            title="بلّغ"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 21V4m0 1h13l-2.5 4L17 13H4" />
            </svg>
          </button>
        )}
      </div>
      {/* private notes panel — author's eyes only (endpoint 404s others) */}
      {notesOpen && (
        <div className="dark-scope">
          {notes === null && (
            <p className="text-xs mt-3" style={{ color: "var(--mj-muted)" }}>جارٍ التحميل…</p>
          )}
          {notes !== null && notes.length === 0 && (
            <p className="text-xs mt-3" style={{ color: "var(--mj-muted)" }}>لا ملاحظات.</p>
          )}
          {(notes || []).map((review) => (
            <FushaFeedback key={review.id} feedback={review} />
          ))}
        </div>
      )}
    </article>
  );
}
